import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, map, catchError } from 'rxjs';
import { GlobalVariable } from '../../global';
import { environment } from '../../../environments/environment';

// バッチ管理関連のインターフェース
export interface BatchInfo {
    id: string;
    name: string;
    status: 'success' | 'failure' | 'running' | 'pending';
    lastExecutionTime?: Date;
    nextExecutionTime?: Date;
    message?: string;
}

export interface BatchExecutionResult {
    batchId: string;
    success: boolean;
    message: string;
    executionTime: Date;
}

// 手動実行可能バッチのインターフェース
export interface ManualBatchItem {
    batchName: string;
    jobName: string;
    description?: string;
}

// バッチ実行ログのインターフェース
export interface BatchExecutionLog {
    jobName: string;
    lastRunAt: string;
    result: string;
    lastError: string;
}

// DTJExport実行ログのインターフェース
export interface DTJExecutionLog {
    finishedAt: string;
    result: string;
    successCount: number;
    failedCount: number;
}

// TSReminder実行ログのインターフェース
export interface TSReminderExecutionLog {
    finishedAt: string;
    result: string;
    ascCount: number;
    apvCount: number;
}

// メンテナンス詳細のインターフェース
export interface MaintenanceDetails {
    noticeStartDate?: string;
    noticeEndDate?: string;
    noticeText?: string;
    suspendStartDate?: string;
    suspendEndDate?: string;
    alternativeURL?: string;
}

// テンプレートのインターフェース
export interface Template {
    templateId: string;
    templateName: string;
    templateType: string | number;
}

// メンテナンスIDアイテムのインターフェース（ドロップダウン用）
export interface MaintenanceIdItem {
    text: string;   // 表示テキスト（MaintenanceID:NoticeText最初の10文字）
    value: number;  // 値（MaintenanceID）
}

// テンプレート詳細のインターフェース
export interface TemplateDetails {
    templateName?: string;
    templateType?: number;
    title?: string;
    content?: string;
    target?: number;
    defaultSender?: string;
}

// ユーザー検索結果のインターフェース（SQLクエリに基づく）
export interface UserSearchResult {
    userID: string;         // UserID
    userName: string;        // UserName
    repID: string;          // RepID
    repNumber: string;      // RepNumber
    enabled: boolean;       // Enabled
    readWrite: boolean;     // ReadWrite (書込み権限)
    approver: boolean;      // Approver
    manager: boolean;       // Manager
    adminLevel: number;     // AdminLevel
    createDate: string;     // CreateDate (最終更新日時)
    email: string;          // 検索に使用したメールアドレス
}

// 管理者リスト用のインターフェース（SQLクエリに基づく）
export interface ManagerData {
    userID: string;         // UserID
    userName: string;       // UserName
    adminLevel: number;     // AdminLevel
    readWrite: boolean;     // ReadWrite (書込み権限)
    email: string;          // Email
    createDate: string;     // CreateDate (作成日時)
    updateDate: string;     // UpdateDate (更新日時)
}

// 管理レベル変更履歴用のインターフェース(SQLクエリに基づく)
export interface ManagementLevelHistory {
    userID: string;         // UserID
    userName: string;       // UserName
    newLevel: number;       // NewLevel
    newReadWrite: boolean;  // NewReadWrite
    ticketNo: string;       // TicketNo (SR番号)
    operaterID: string;     // OperaterID (処理者ID)
    createDate: string;     // CreateDate (処理日時)
}

// ロボット用IDのインターフェース
export interface RobotIdItem {
    loginId: string;        // LoginID
    registeredDate: string; // CreateDate
}

// システム設定のインターフェース(cnf.ST_System SystemID=1)
export interface SystemSettings {
    alertMailAddress: string;           // AlertMailAddress - システムアラート送信先
    supportMailAddress: string;         // SupportMailAddress - サポートアラート送信先
    defaultPassword: string;            // DefaultPassword - 初期パスワード
    passwordExpireDay: number;          // PasswordExpireDay - パスワード有効期間
    passwordExpireWarningDay: number;   // PasswordExpireWarningDay - パスワード更新予告日
}

// カレンダー日付のインターフェース
export interface CalendarDay {
    day: number;
    date: Date;
    isToday: boolean;
    isHoliday: boolean;
    isReminder: boolean;
    isOtherMonth: boolean;
    isSelected: boolean;
    isSunday: boolean;
    isSaturday: boolean;
}

// 祝日のインターフェース
export interface Holiday {
    date: string;  // YYYY-MM-DD format
}

// リマインド月のインターフェース
export interface ReminderMonth {
    month: string;
    monthNumber: number;
    day1: string;
    day2: string;
    day3: string;
    day4: string;
}

// リマインド日のインターフェース
export interface Reminder {
    remindDate: string;  // YYYY-MM-DD format
}

// バックエンドAPIのレスポンスラッパー
interface ApiResponse<T> {
    status: boolean;
    statusCode: number;
    data: T;
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class SysManagementService {
    private apiUrl = environment.useApiGateway
        ? `${environment.apiGatewayUrl}/sysmanagement`
        : GlobalVariable.BASE_URL + '/SysManagement';

    constructor(private http: HttpClient) { }

    // バッチ状況一覧を取得
    getBatchStatusList(): Observable<BatchInfo[]> {
        return this.http.get<BatchInfo[]>(`${this.apiUrl}/batch/status`);
    }

    // 手動実行可能バッチ一覧を取得
    getManualBatchList(): Observable<ManualBatchItem[]> {
        return this.http.get<ManualBatchItem[]>(`${this.apiUrl}/batch/manual-list`).pipe(
            catchError(() => of([]))
        );
    }

    // バッチを手動実行
    executeBatch(batchId: string): Observable<BatchExecutionResult> {
        return this.http.post<BatchExecutionResult>(`${this.apiUrl}/batch/execute`, { batchId });
    }

    // バッチ詳細情報を取得
    getBatchDetail(batchId: string): Observable<BatchInfo> {
        return this.http.get<BatchInfo>(`${this.apiUrl}/batch/${batchId}`);
    }

    // バッチ実行ログを取得
    getBatchExecutionLogs(selectedDate: string, resultType: 'all' | 'success' | 'failure'): Observable<BatchExecutionLog[]> {
        return this.http.get<ApiResponse<BatchExecutionLog[]>>(
            `${this.apiUrl}/batch/execution-logs?selectedDate=${selectedDate}&resultType=${resultType}`
        ).pipe(
            map(response => response.data || []),
            catchError(() => {
                return of([]);
            })
        );
    }

    // DTJExport実行ログを取得
    getDTJExecutionLogs(selectedDate: string): Observable<DTJExecutionLog[]> {
        return this.http.get<ApiResponse<DTJExecutionLog[]>>(
            `${this.apiUrl}/batch/dtj-export-logs?selectedDate=${selectedDate}`
        ).pipe(
            map(response => response.data || []),
            catchError(() => {
                return of([]);
            })
        );
    }

    // TSReminder実行ログを取得（過去10日間、送信数または失敗数が1件以上）
    getTSReminderExecutionLogs(): Observable<TSReminderExecutionLog[]> {
        return this.http.get<ApiResponse<TSReminderExecutionLog[]>>(
            `${this.apiUrl}/batch/tsreminder-logs`
        ).pipe(
            map(response => response.data || []),
            catchError(() => {
                return of([]);
            })
        );
    }

    // 現在の告知を取得
    getCurrentNotice(): Observable<string> {
        // USE DTNEO;
        // DECLARE @CheckDate datetime = GETDATE();
        // SELECT NoticeText FROM cnf.ST_Maintenance
        // WHERE NoticeStartDate <= @CheckDate AND NoticeEndDate >= @CheckDate AND Valid = 1;
        return this.http.get<{noticeText: string}>(`${this.apiUrl}/maintenance/current-notice`)
            .pipe(
                map(response => response.noticeText || ''),
                catchError(error => {
                    console.error('現在の告知取得エラー:', error);
                    return of('');
                })
            );
    }

    // メンテナンスIDリストを取得（ドロップダウン用）
    getMaintenanceIds(): Observable<MaintenanceIdItem[]> {
        // USE DTNEO;
        // SELECT MaintenanceID, NoticeText FROM cnf.ST_Maintenance ORDER BY MaintenanceID DESC;
        return this.http.get<MaintenanceIdItem[]>(`${this.apiUrl}/maintenance/ids`)
            .pipe(
                catchError(error => {
                    console.error('メンテナンスID取得エラー:', error);
                    return of([]);
                })
            );
    }

    // メンテナンス詳細を取得
    getMaintenanceDetails(maintenanceId: string): Observable<MaintenanceDetails | null> {
        // USE DTNEO;
        // SELECT NoticeStartDate, NoticeEndDate, NoticeText, SuspendStartDate, SuspendEndDate, AlternativeURL
        // FROM cnf.ST_Maintenance WHERE MaintenanceID = @MaintenanceID;
        return this.http.get<MaintenanceDetails>(`${this.apiUrl}/maintenance/${maintenanceId}`)
            .pipe(
                catchError(error => {
                    console.error('メンテナンス詳細取得エラー:', error);
                    return of(null);
                })
            );
    }

    // テンプレートリストを取得
    getTemplates(): Observable<Template[]> {
        // USE DTNEO;
        // SELECT TemplateType, TemplateID FROM cnf.ST_Template WHERE TemplateType = 2 ORDER BY TemplateID DESC;
        return this.http.get<Template[]>(`${this.apiUrl}/templates`)
            .pipe(
                catchError(error => {
                    console.error('テンプレートリスト取得エラー:', error);
                    return of([]);
                })
            );
    }

    // テンプレート内容を取得
    getTemplateContent(templateId: string): Observable<string | null> {
        // USE DTNEO;
        // SELECT Content FROM cnf.ST_Template WHERE TemplateID = @TemplateID;
        return this.http.get<{content: string}>(`${this.apiUrl}/templates/${templateId}/content`)
            .pipe(
                map(response => response.content || null),
                catchError(error => {
                    console.error('テンプレート内容取得エラー:', error);
                    return of(null);
                })
            );
    }

    // メンテナンス記録を更新
    updateMaintenanceRecord(maintenanceId: string, data: any): Observable<any> {
        // DECLARE @ChangeDate datetime = GETDATE();
        // UPDATE cnf.ST_Maintenance SET
        // NoticeStartDate = @NoticeStartDate,
        // NoticeEndDate = @NoticeEndDate,
        // NoticeText = @NoticeText,
        // SuspendStartDate = @SuspendStartDate,
        // SuspendEndDate = @SuspendEndDate,
        // AlternativeURL = @AlternativeURL,
        // Valid = 1,
        // UpdateDate = @ChangeDate
        // WHERE MaintenanceID = @MaintenanceID;
        return this.http.put(`${this.apiUrl}/maintenance/${maintenanceId}`, data);
    }

    // メンテナンス記録を挿入
    insertMaintenanceRecord(data: any): Observable<any> {
        // DECLARE @ChangeDate datetime = GETDATE();
        // INSERT INTO cnf.ST_Maintenance(
        // NoticeStartDate, NoticeEndDate, NoticeText,
        // SuspendStartDate, SuspendEndDate, AlternativeURL,
        // Valid, CreateDate, UpdateDate
        // ) VALUES (
        // @NoticeStartDate, @NoticeEndDate, @NoticeText,
        // @SuspendStartDate, @SuspendEndDate, @AlternativeURL,
        // 1, @ChangeDate, @ChangeDate
        // );
        return this.http.post(`${this.apiUrl}/maintenance`, data);
    }

    // メンテナンス記録を削除
    deleteMaintenanceRecord(maintenanceId: string): Observable<any> {
        // DELETE FROM cnf.ST_Maintenance WHERE MaintenanceID = @MaintenanceID;
        return this.http.delete(`${this.apiUrl}/maintenance/${maintenanceId}`);
    }

    // 編集可能テンプレートリストを取得
    getEditableTemplates(): Observable<Template[]> {
        // USE DTNEO;
        // SELECT TemplateType, TemplateID FROM cnf.ST_Template ORDER BY TemplateID DESC;
        return this.http.get<Template[]>(`${this.apiUrl}/templates/editable`)
            .pipe(
                catchError(error => {
                    console.error('編集可能テンプレートリスト取得エラー:', error);
                    return of([]);
                })
            );
    }

    // テンプレート詳細を取得
    getTemplateDetails(templateId: string): Observable<TemplateDetails | null> {
        // USE DTNEO;
        // SELECT TemplateName, TemplateType, Title, Content, Target, DefaultSender
        // FROM cnf.ST_Template WHERE TemplateID = @TemplateID;
        return this.http.get<TemplateDetails>(`${this.apiUrl}/templates/${templateId}/details`)
            .pipe(
                catchError(error => {
                    console.error('テンプレート詳細取得エラー:', error);
                    return of(null);
                })
            );
    }

    // テンプレート更新
    updateTemplate(templateId: string, data: any): Observable<any> {
        // DECLARE @ChangeDate datetime = GETDATE();
        // UPDATE cnf.ST_Template SET
        // TemplateName = @TemplateName,
        // TemplateType = @TemplateType,
        // Title = @Title,
        // Content = @Content,
        // Target = @Target,
        // DefaultSender = @DefaultSender,
        // UpdateDate = @ChangeDate
        // WHERE TemplateID = @TemplateID;
        return this.http.put(`${this.apiUrl}/templates/${templateId}`, data);
    }

    // テンプレート挿入
    insertTemplate(data: any): Observable<any> {
        // DECLARE @ChangeDate datetime = GETDATE();
        // INSERT INTO cnf.ST_Template (
        // TemplateName, TemplateType, Title,
        // Content, Target, DefaultSender, CreateDate, UpdateDate
        // ) VALUES (
        // @TemplateName, @TemplateType, @Title,
        // @Content, @Target, @DefaultSender, @ChangeDate, @ChangeDate
        // );
        return this.http.post(`${this.apiUrl}/templates`, data);
    }

    // テンプレート削除
    deleteTemplate(templateId: string): Observable<any> {
        // DELETE FROM cnf.ST_Template WHERE TemplateID = @TemplateID;
        return this.http.delete(`${this.apiUrl}/templates/${templateId}`);
    }

    // 管理者リストを取得
    getManagers(): Observable<any[]> {
        // 実装時のAPIコール
        // return this.http.get<any[]>(`${this.apiUrl}/users/managers`);

        // 開発用モックデータ（添付画像の表データを参考）
        const mockManagers = [
            { userId: '-142386', name: '徳川 家康', level: 3, canApply: true, email: 'tokugawa@manpowergroup.jp' },
            { userId: '-151168', name: '小田 信長', level: 3, canApply: true, email: 'noda@manpowergroup.jp' },
            { userId: '-153296', name: '豊臣 秀吉', level: 3, canApply: true, email: 'htoyotomi@manpowergroup.jp' },
            { userId: '-158452', name: '明智 光秀', level: 3, canApply: true, email: 'makechi@manpowergroup.jp' },
            { userId: '-160542', name: '浅井 長政', level: 3, canApply: true, email: 'nasai@manpowergrou.jp' }
        ];

        console.log('管理者リスト取得');
        return of(mockManagers);
    }

    // メールアドレスでユーザー検索
    getUserByEmail(email: string, employeesOnly: boolean = false): Observable<UserSearchResult | null> {
        const url = `${this.apiUrl}/user-by-email?email=${encodeURIComponent(email)}&employeesOnly=${employeesOnly}`;
        return this.http.get<ApiResponse<UserSearchResult>>(url).pipe(
            map(response => {
                if (response && response.data) {
                    return response.data;
                }
                return null;
            }),
            catchError(error => {
                if (error.status === 404) {
                    return of(null);
                }
                console.error('ユーザー検索エラー:', error);
                throw error;
            })
        );
    }

    // ユーザー管理レベル更新
    updateUserManagementLevel(data: any): Observable<any> {
        const url = `${this.apiUrl}/update-user-management-level`;
        return this.http.put<ApiResponse<boolean>>(url, data).pipe(
            map(response => {
                if (response && response.status) {
                    return { success: true, message: response.message };
                }
                throw new Error(response?.message || 'ユーザー管理レベルの更新に失敗しました');
            }),
            catchError(error => {
                console.error('ユーザー管理レベル更新エラー:', error);
                throw error;
            })
        );
    }

    // 管理者リスト取得（レベル指定）
    getManagersByLevels(levels: number[]): Observable<ManagerData[]> {
        const levelsParam = levels.join(',');
        return this.http.get<ApiResponse<ManagerData[]>>(`${this.apiUrl}/managers?levels=${levelsParam}`)
            .pipe(
                map(response => {
                    if (response && response.data) {
                        // バックエンドはパスカルケース(UserID)、フロントエンドはキャメルケース(userID)
                        // レスポンスのプロパティ名を変換
                        return response.data.map((item: any) => ({
                            userID: item.userID || item.UserID,
                            userName: item.userName || item.UserName,
                            adminLevel: item.adminLevel !== undefined ? item.adminLevel : item.AdminLevel,
                            readWrite: item.readWrite !== undefined ? item.readWrite : item.ReadWrite,
                            email: item.email || item.Email,
                            createDate: item.createDate || item.CreateDate,
                            updateDate: item.updateDate || item.UpdateDate
                        }));
                    }
                    return [];
                }),
                catchError(error => {
                    console.error('管理者リスト取得エラー:', error);
                    throw error;
                })
            );
    }

    // 管理レベル変更履歴取得(年指定)
    getManagementLevelHistory(year: number): Observable<ManagementLevelHistory[]> {
        return this.http.get<ApiResponse<ManagementLevelHistory[]>>(`${this.apiUrl}/management-level-history?year=${year}`)
            .pipe(
                map(response => {
                    // バックエンドはパスカルケース(UserID)、フロントエンドはキャメルケース(userID)
                    // レスポンスのプロパティ名を変換
                    return (response.data || []).map((item: any) => ({
                        userID: item.userID || item.UserID,
                        userName: item.userName || item.UserName,
                        newLevel: item.newLevel || item.NewLevel,
                        newReadWrite: item.newReadWrite !== undefined ? item.newReadWrite : item.NewReadWrite,
                        ticketNo: item.ticketNo || item.TicketNo,
                        operaterID: item.operaterID || item.OperaterID,
                        createDate: item.createDate || item.CreateDate
                    }));
                })
            );
    }

    // カレンダー用の祝日を取得
    getCalendarHolidays(year: number, month: number): Observable<any[]> {
        return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/calendar-holidays?year=${year}&month=${month}`)
            .pipe(map(response => response.data));
    }

    // カレンダー用のリマインド日を取得
    getCalendarReminders(year: number, month: number): Observable<any[]> {
        return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/calendar-reminders?year=${year}&month=${month}`)
            .pipe(map(response => response.data));
    }

    // システム設定を取得(cnf.ST_System WHERE SystemID = 1)
    getSystemSettings(): Observable<SystemSettings> {
        return this.http.get<ApiResponse<SystemSettings>>(`${this.apiUrl}/system-setting`)
            .pipe(map(response => response.data));
    }

    // システム設定を保存(cnf.ST_System WHERE SystemID = 1)
    saveSystemSettings(settings: SystemSettings): Observable<any> {
        return this.http.post<ApiResponse<any>>(`${this.apiUrl}/system-setting`, settings)
            .pipe(map(response => response.data));
    }

    // 祝日を年で取得(cnf.ST_Holiday WHERE YEAR(HolidayDate) = @year)
    getHolidaysByYear(year: number): Observable<any[]> {
        return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/holidays?year=${year}`)
            .pipe(map(response => response.data));
    }

    // 祝日を保存(cnf.ST_Holiday - 該当年を全削除してから新規登録)
    saveHolidays(year: number, holidays: any[]): Observable<any> {
        // holidaysから日付文字列の配列を作成
        const dates = holidays.map(h => h.date);
        return this.http.post<ApiResponse<any>>(`${this.apiUrl}/holidays`, { year, dates })
            .pipe(map(response => response.data));
    }

    // リマインド日を年で取得(cnf.ST_Remind WHERE YEAR(RemindDate) = @year)
    getRemindersByYear(year: number): Observable<any[]> {
        return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/reminders?year=${year}`)
            .pipe(map(response => response.data));
    }

    // リマインド日を保存(cnf.ST_Remind - 該当年を全削除してから新規登録)
    saveReminders(year: number, reminders: any[]): Observable<any> {
        // remindersから日付文字列の配列を作成
        const remindDates = reminders.map(r => r.remindDate);
        return this.http.post<ApiResponse<any>>(`${this.apiUrl}/reminders`, { year, remindDates })
            .pipe(map(response => response.data));
    }

    // ロボット用ID一覧取得
    // SELECT LoginID, CreateDate FROM users WHERE RepID = 'Robo' ORDER BY CreateDate DESC
    getRobotIds(): Observable<RobotIdItem[]> {
        return this.http.get<ApiResponse<RobotIdItem[]>>(`${this.apiUrl}/robot-ids`)
            .pipe(
                map(response => {
                    return (response.data || []).map((item: any) => ({
                        loginId: item.loginId || item.LoginID,
                        registeredDate: item.registeredDate || item.RegisteredDate || item.CreateDate
                    }));
                }),
                catchError(() => of([]))
            );
    }

    // ロボット用IDを削除
    // DELETE FROM Users WHERE LoginID = loginId AND RepID = 'Robo'
    deleteRobotId(loginId: string): Observable<{ success: boolean; message: string }> {
        return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/robot-ids/${encodeURIComponent(loginId)}`)
            .pipe(
                map(response => ({ success: response.status, message: response.message || '削除しました' })),
                catchError((error: any) => {
                    const message = error?.error?.message || 'ロボット用IDの削除に失敗しました';
                    return of({ success: false, message });
                })
            );
    }

    // ロボット用IDを新規登録
    registerRobotId(loginId: string, srNumber: string): Observable<{ success: boolean; message: string }> {
        return this.http.post<ApiResponse<any>>(`${this.apiUrl}/robot-ids`, { loginId, srNumber })
            .pipe(
                map(response => ({ success: response.status, message: response.message || 'IDを登録しました。' })),
                catchError((error: any) => {
                    const message = error?.error?.message || 'ロボット用IDの登録に失敗しました';
                    return of({ success: false, message });
                })
            );
    }
}